/**
 * JSON Pointer (RFC 6901) utilities.
 *
 * The zoom-in navigation is a pointer in disguise: "/pages/0/blocks/2/content/1"
 * is both a URL segment and a path into the data. Centralizing this prevents
 * the bugs that come from ad-hoc string splitting all over the codebase.
 */

import type { JsonValue } from '@bliksys/shared';

import { InvalidPointerError } from './errors.js';

/** A JSON Pointer, always starting with '/' or the empty string (document root). */
export type JsonPointer = string;

const ESCAPE_TILDE = /~/g;
const ESCAPE_SLASH = /\//g;
const UNESCAPE_1 = /~1/g;
const UNESCAPE_0 = /~0/g;

/** Escape a single path segment per RFC 6901 (~ -> ~0, / -> ~1). Order matters. */
export function escapeSegment(segment: string): string {
  return segment.replace(ESCAPE_TILDE, '~0').replace(ESCAPE_SLASH, '~1');
}

/** Inverse of escapeSegment. Order matters (~1 first, then ~0). */
export function unescapeSegment(segment: string): string {
  return segment.replace(UNESCAPE_1, '/').replace(UNESCAPE_0, '~');
}

/** Parse a pointer into its segments. Root is an empty array. */
export function parsePointer(pointer: JsonPointer): string[] {
  if (pointer === '') return [];
  if (!pointer.startsWith('/')) {
    throw new InvalidPointerError(pointer);
  }
  return pointer.slice(1).split('/').map(unescapeSegment);
}

/** Build a pointer from segments. */
export function buildPointer(segments: readonly string[]): JsonPointer {
  if (segments.length === 0) return '';
  return '/' + segments.map(escapeSegment).join('/');
}

/** Append one segment to a pointer. Works for both string keys and array indices. */
export function appendPointer(pointer: JsonPointer, segment: string | number): JsonPointer {
  return pointer + '/' + escapeSegment(String(segment));
}

/** Parent pointer, or null if already at root. */
export function parentPointer(pointer: JsonPointer): JsonPointer | null {
  if (pointer === '') return null;
  const lastSlash = pointer.lastIndexOf('/');
  if (lastSlash <= 0) return '';
  return pointer.slice(0, lastSlash);
}

/** Last segment of a pointer (unescaped), or null at root. */
export function lastSegment(pointer: JsonPointer): string | null {
  if (pointer === '') return null;
  const segments = parsePointer(pointer);
  return segments[segments.length - 1] ?? null;
}

/**
 * Get a value at a pointer. Returns undefined if the path doesn't exist.
 * Does NOT throw on missing paths — missing is a valid answer when the user
 * is editing a freshly-added item.
 */
export function getAtPointer(root: JsonValue, pointer: JsonPointer): JsonValue | undefined {
  if (pointer === '') return root;
  const segments = parsePointer(pointer);
  let current: JsonValue | undefined = root;
  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;
    if (Array.isArray(current)) {
      const index = toArrayIndex(segment);
      if (index === null || index >= current.length) return undefined;
      current = current[index];
    } else if (typeof current === 'object') {
      if (!Object.prototype.hasOwnProperty.call(current, segment)) return undefined;
      current = current[segment];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Immutably set a value at a pointer. Creates intermediate containers where
 * needed (arrays if the next segment is a valid index, objects otherwise).
 *
 * This is the core of the edit operation. Returning a new tree instead of
 * mutating means the UI can do structural sharing and cheap diffing.
 */
export function setAtPointer(root: JsonValue, pointer: JsonPointer, value: JsonValue): JsonValue {
  if (pointer === '') return value;
  const segments = parsePointer(pointer);
  return setRecursive(root, segments, 0, value);
}

function setRecursive(
  current: JsonValue | undefined,
  segments: readonly string[],
  index: number,
  value: JsonValue,
): JsonValue {
  const segment = segments[index];
  if (segment === undefined) return value;
  const isLast = index === segments.length - 1;

  // Decide container shape based on whether the segment looks like an array index.
  // Preserve existing shape if present; otherwise infer from the segment.
  const asArrayIndex = toArrayIndex(segment);
  const useArray = Array.isArray(current) || (current === undefined && asArrayIndex !== null);

  if (useArray) {
    const arr = Array.isArray(current) ? [...current] : [];
    if (asArrayIndex === null) {
      // Segment isn't a valid index but we're in an array — treat as append-ish
      // would be ambiguous. Throw instead so callers get a clear error.
      throw new InvalidPointerError(buildPointer(segments.slice(0, index + 1)));
    }
    const nextValue = isLast ? value : setRecursive(arr[asArrayIndex], segments, index + 1, value);
    arr[asArrayIndex] = nextValue;
    return arr;
  }

  const obj: Record<string, JsonValue> =
    current !== null && typeof current === 'object' && !Array.isArray(current)
      ? { ...current }
      : {};
  obj[segment] = isLast ? value : setRecursive(obj[segment], segments, index + 1, value);
  return obj;
}

/** Immutably delete a value at a pointer. No-op if path doesn't exist. */
export function deleteAtPointer(root: JsonValue, pointer: JsonPointer): JsonValue {
  if (pointer === '') return root; // can't delete root
  const segments = parsePointer(pointer);
  return deleteRecursive(root, segments, 0) ?? root;
}

function deleteRecursive(
  current: JsonValue | undefined,
  segments: readonly string[],
  index: number,
): JsonValue | undefined {
  const segment = segments[index];
  if (segment === undefined || current === undefined || current === null) return current;
  const isLast = index === segments.length - 1;

  if (Array.isArray(current)) {
    const idx = toArrayIndex(segment);
    if (idx === null || idx >= current.length) return current;
    if (isLast) {
      const arr = [...current];
      arr.splice(idx, 1);
      return arr;
    }
    const arr = [...current];
    const next = deleteRecursive(arr[idx], segments, index + 1);
    if (next === undefined) return current;
    arr[idx] = next;
    return arr;
  }

  if (typeof current === 'object') {
    const obj = current;
    if (!Object.prototype.hasOwnProperty.call(obj, segment)) return current;
    if (isLast) {
      const { [segment]: _removed, ...rest } = obj;
      return rest;
    }
    const next = deleteRecursive(obj[segment], segments, index + 1);
    if (next === undefined) return current;
    return { ...obj, [segment]: next };
  }

  return current;
}

function toArrayIndex(segment: string): number | null {
  // RFC 6901: array indices must be non-negative integers without leading zeros
  // (except "0" itself). "-" refers to the element past the end, we don't support
  // it for reads but do for appends handled separately in the engine.
  if (segment === '0') return 0;
  if (!/^[1-9]\d*$/.test(segment)) return null;
  const n = Number(segment);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * Atomically rename a key within an object: read the value at `oldPointer`,
 * delete it, and set the same value at `newPointer`. Both must share the
 * same parent (they only differ in the last segment); otherwise this is
 * really a delete + a separate set, which the caller should compose.
 *
 * Returns the original root unchanged if the rename can't be applied
 * (value missing, pointer escapes the root, parents differ). Used by the
 * schema editor to rename properties without losing configured constraints.
 */
export function renameKeyAtPointer(
  root: JsonValue,
  oldPointer: JsonPointer,
  newPointer: JsonPointer,
): JsonValue {
  if (oldPointer === newPointer) return root;
  const value = getAtPointer(root, oldPointer);
  if (value === undefined) return root;
  const afterDelete = deleteAtPointer(root, oldPointer);
  return setAtPointer(afterDelete, newPointer, value);
}

/**
 * Reorder a key within an object map. The object at `parentPointer` is
 * rebuilt with `key` repositioned: before `beforeKey`, or to the end if
 * `beforeKey` is null. Other keys preserve their relative order. Returns
 * the original root unchanged if the parent isn't an object, the key
 * doesn't exist, or `beforeKey` is named but doesn't exist.
 *
 * Used by the schema editor to drag-reorder `properties` map entries —
 * because Bliksys now relies on insertion order for field display order.
 */
export function moveObjectKeyAtPointer(
  root: JsonValue,
  parentPointer: JsonPointer,
  key: string,
  beforeKey: string | null,
): JsonValue {
  const obj = getAtPointer(root, parentPointer);
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return root;
  const entries = Object.entries(obj);
  const original = entries.find(([k]) => k === key);
  if (!original) return root;
  const without = entries.filter(([k]) => k !== key);
  let next: [string, JsonValue][];
  if (beforeKey === null) {
    next = [...without, original];
  } else {
    const idx = without.findIndex(([k]) => k === beforeKey);
    if (idx < 0) return root;
    next = [...without.slice(0, idx), original, ...without.slice(idx)];
  }
  const reordered: Record<string, JsonValue> = {};
  for (const [k, v] of next) reordered[k] = v;
  return setAtPointer(root, parentPointer, reordered);
}

/**
 * Reorder an item within an array. `arrayPointer` points at the array
 * itself; `from` and `to` are indices. Out-of-range, non-array, or
 * no-op moves return the original root unchanged.
 */
export function moveArrayItemAtPointer(
  root: JsonValue,
  arrayPointer: JsonPointer,
  from: number,
  to: number,
): JsonValue {
  if (from === to) return root;
  const array = getAtPointer(root, arrayPointer);
  if (!Array.isArray(array)) return root;
  if (from < 0 || from >= array.length || to < 0 || to >= array.length) return root;
  const reordered = [...array];
  const [item] = reordered.splice(from, 1);
  reordered.splice(to, 0, item as JsonValue);
  return setAtPointer(root, arrayPointer, reordered);
}
